import * as THREE from 'three';

/** Yard props: a tree for squirrels to escape up, a fence for cats to sit on. */
export class Yard {
  readonly tree = new THREE.Group();
  readonly fence = new THREE.Group();
  /** where a climbing squirrel ends up */
  readonly treeTop = new THREE.Vector3();
  /** tops of a few fence posts (world), for cans */
  readonly postTops: THREE.Vector3[] = [];
  /** every trunk (x, z, radius) for colliders */
  readonly trunks: { x: number; z: number; r: number }[] = [];
  /** just inside / outside the gate, on the ground */
  readonly gateInside = new THREE.Vector3();
  readonly gateOutside = new THREE.Vector3();
  /** fence top rail: from `fenceA` to `fenceB` at height `fenceH` */
  readonly fenceA = new THREE.Vector3();
  readonly fenceB = new THREE.Vector3();
  readonly fenceH = 0.95;
  /** the gate opening on the rail line (world), a little wider than the posts so a walker fits through */
  readonly gateA = new THREE.Vector3();
  readonly gateB = new THREE.Vector3();
  /** the streetlamp: comes on at real dusk, so you can still play at night */
  readonly lamp = new THREE.Group();
  private lampLight: THREE.SpotLight;
  private lampGlow: THREE.PointLight;
  private bulbMat: THREE.MeshStandardMaterial;

  constructor(scene: THREE.Object3D) {
    const bark = new THREE.MeshStandardMaterial({ color: 0x6b4a2e, roughness: 1 });
    const leaf = new THREE.MeshStandardMaterial({ color: 0x4f7a3a, roughness: 0.95 });
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.24, 2.2, 10), bark);
    trunk.position.y = 1.1;
    trunk.castShadow = true;
    this.tree.add(trunk);
    for (const [x, y, z, r] of [
      [0, 2.6, 0, 1.1],
      [0.7, 2.2, 0.3, 0.8],
      [-0.6, 2.3, -0.4, 0.85],
      [0.1, 3.2, -0.2, 0.7],
    ] as const) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(r, 12, 9), leaf);
      m.position.set(x, y, z);
      m.castShadow = true;
      this.tree.add(m);
    }
    this.tree.position.set(-4.4, 0, -5.4);
    scene.add(this.tree);
    this.trunks.push({ x: -4.4, z: -5.4, r: 0.22 });
    // a couple more trees further out: something to pan to
    for (const [x, z, sc] of [
      [8.5, -9, 1.3],
      [-9, 3, 1.1],
      [7, 6, 0.9],
    ] as const) {
      const t = this.tree.clone();
      t.position.set(x, 0, z);
      t.scale.setScalar(sc);
      t.rotation.y = x;
      scene.add(t);
      this.trunks.push({ x, z, r: 0.22 * sc });
    }
    this.treeTop.copy(this.tree.position).add(new THREE.Vector3(0, 2.4, 0));

    // fence along the back-right, three rails and posts
    const wood = new THREE.MeshStandardMaterial({ color: 0x9c7b56, roughness: 1 });
    const len = 6;
    for (let i = 0; i <= 6; i++) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.09, this.fenceH + 0.1, 0.09), wood);
      post.position.set(-len / 2 + i, (this.fenceH + 0.1) / 2, 0);
      post.castShadow = true;
      this.fence.add(post);
    }
    // rails, with a gap for the gate between the 3rd and 4th posts
    const gateX0 = -len / 2 + 2;
    const gateX1 = -len / 2 + 3;
    for (const y of [0.3, 0.62, this.fenceH]) {
      for (const [x0, x1] of [
        [-len / 2, gateX0],
        [gateX1, len / 2],
      ] as const) {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(x1 - x0, 0.06, 0.06), wood);
        rail.position.set((x0 + x1) / 2, y, 0);
        rail.castShadow = true;
        this.fence.add(rail);
      }
    }
    // the gate: a lighter frame with a diagonal brace, hung a little lower than the rails
    const gateWood = new THREE.MeshStandardMaterial({ color: 0xb59468, roughness: 1 });
    const gw = gateX1 - gateX0 - 0.12;
    const gate = new THREE.Group();
    for (const y of [0.28, 0.6, 0.9]) {
      const r = new THREE.Mesh(new THREE.BoxGeometry(gw, 0.06, 0.05), gateWood);
      r.position.y = y;
      gate.add(r);
    }
    for (const x of [-gw / 2 + 0.03, gw / 2 - 0.03]) {
      const v = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.72, 0.05), gateWood);
      v.position.set(x, 0.59, 0);
      gate.add(v);
    }
    const brace = new THREE.Mesh(new THREE.BoxGeometry(Math.hypot(gw, 0.62), 0.05, 0.04), gateWood);
    brace.position.y = 0.59;
    brace.rotation.z = Math.atan2(0.62, gw);
    gate.add(brace);
    gate.position.set((gateX0 + gateX1) / 2, 0, 0);
    gate.children.forEach((m) => (m.castShadow = true));
    this.fence.add(gate);
    this.fence.position.set(5.5, 0, -7.8);
    this.fence.rotation.y = -0.35;
    scene.add(this.fence);
    this.fence.updateMatrixWorld(true);
    this.fenceA.set(-len / 2, this.fenceH, 0).applyMatrix4(this.fence.matrixWorld);
    this.fenceB.set(len / 2, this.fenceH, 0).applyMatrix4(this.fence.matrixWorld);
    this.gateA.set(gateX0 - 0.18, 0, 0).applyMatrix4(this.fence.matrixWorld);
    this.gateB.set(gateX1 + 0.18, 0, 0).applyMatrix4(this.fence.matrixWorld);
    for (const i of [0, 1, 4, 6]) this.postTops.push(new THREE.Vector3(-len / 2 + i, this.fenceH + 0.05, 0).applyMatrix4(this.fence.matrixWorld));
    this.gateInside.set((gateX0 + gateX1) / 2, 0, 0.9).applyMatrix4(this.fence.matrixWorld);
    this.gateOutside.set((gateX0 + gateX1) / 2, 0, -0.7).applyMatrix4(this.fence.matrixWorld);

    // streetlamp: iron post, curved arm, warm bulb under a hood, lighting the play area
    const iron = new THREE.MeshStandardMaterial({ color: 0x2b2f33, roughness: 0.6, metalness: 0.5 });
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 2.6, 10), iron);
    post.position.y = 1.3;
    post.castShadow = true;
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 0.12, 12), iron);
    base.position.y = 0.06;
    const arm = new THREE.Mesh(new THREE.TorusGeometry(0.35, 0.03, 8, 16, Math.PI / 2), iron);
    arm.position.set(0.35, 2.6, 0);
    arm.rotation.z = Math.PI / 2;
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.16, 12, 1, true), iron);
    hood.position.set(0.7, 2.62, 0);
    this.bulbMat = new THREE.MeshStandardMaterial({ color: 0xfff2cc, emissive: 0xffd27a, emissiveIntensity: 0 });
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 10), this.bulbMat);
    bulb.position.set(0.7, 2.55, 0);
    this.lamp.add(base, post, arm, hood, bulb);
    this.lampLight = new THREE.SpotLight(0xffd9a0, 0, 9, 0.9, 0.6, 1.2);
    this.lampLight.position.set(0.7, 2.55, 0);
    this.lampLight.target.position.set(0.9, 0, 0.3);
    this.lamp.add(this.lampLight, this.lampLight.target);
    this.lampGlow = new THREE.PointLight(0xffd9a0, 0, 4, 1.5);
    this.lampGlow.position.set(0.7, 2.5, 0);
    this.lamp.add(this.lampGlow);
    this.lamp.position.set(1.7, 0, 1.3);
    this.lamp.rotation.y = Math.PI * 0.85; // arm reaches over the bowls / play spot
    scene.add(this.lamp);
  }

  private lights: THREE.Group | null = null;
  /** a string of coloured bulbs along the fence rail (holidays) */
  setLights(on: boolean): void {
    if (!on) {
      if (this.lights) {
        this.fence.remove(this.lights);
        this.lights = null;
      }
      return;
    }
    if (this.lights) return;
    const g = new THREE.Group();
    const colors = [0xff4b4b, 0x4bd36a, 0x4b8bff, 0xffd84b, 0xff7ad9];
    for (let i = 0; i < 25; i++) {
      const c = colors[i % colors.length];
      const b = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 5), new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 1.4 }));
      b.position.set(-3 + (i / 24) * 6, this.fenceH - 0.06 - Math.abs(Math.sin(i * 1.3)) * 0.05, 0.04);
      g.add(b);
    }
    this.fence.add(g);
    this.lights = g;
  }

  /** a worn dirt path through the given points (on the ground) */
  addPath(scene: THREE.Object3D, points: THREE.Vector3[], width = 0.7): void {
    const curve = new THREE.CatmullRomCurve3(points.map((p) => p.clone().setY(0)), false, 'catmullrom', 0.4);
    const n = 60;
    const pos = new Float32Array((n + 1) * 2 * 3);
    const idx: number[] = [];
    const up = new THREE.Vector3(0, 1, 0);
    const side = new THREE.Vector3();
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const p = curve.getPoint(t);
      const tan = curve.getTangent(t).normalize();
      side.crossVectors(up, tan).normalize().multiplyScalar(width / 2);
      pos.set([p.x + side.x, 0.006, p.z + side.z, p.x - side.x, 0.006, p.z - side.z], i * 6);
      if (i < n) {
        const a = i * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    const m = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0xb9a37c, roughness: 1, side: THREE.DoubleSide, transparent: true, opacity: 0.85 }));
    m.receiveShadow = true;
    scene.add(m);
  }

  /** 0 day .. 1 night: the lamp fades up as the real sun goes down */
  setNight(n: number): void {
    this.lampLight.intensity = 45 * n;
    this.lampGlow.intensity = 6 * n;
    this.bulbMat.emissiveIntensity = 2.5 * n;
  }
}
